import React, { createContext, useContext } from 'react';
import { useAuth, AuthUser, AccessRole, PagePermission } from './useAuth';

interface AuthContextValue {
  authUser: AuthUser | null;
  loading: boolean;
  error: string | null;
  effectiveRole: AccessRole;
  roleOverride: AccessRole | null;
  setRoleOverride: (role: AccessRole | null) => void;
  hasPermission: (page: string, action: 'can_view' | 'can_create' | 'can_edit' | 'can_delete') => boolean;
  canView: (page: string) => boolean;
  canCreate: (page: string) => boolean;
  canEdit: (page: string) => boolean;
  canDelete: (page: string) => boolean;
  canEditOwn: (page: string, itemOwnerId?: number) => boolean;
  canDeleteOwn: (page: string, itemOwnerId?: number) => boolean;
  isAdmin: boolean;
  isDeveloper: boolean;
  isViewer: boolean;
  refresh: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}

export type { AuthUser, AccessRole, PagePermission };
